import React from 'react';
import { MessageCircle, Check, Star, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import TrustBar from '../TrustBar';
import { formatMoney, calculateTotal } from '../../utils/formatMoney';

const PriceSummaryCard = ({ selectedVehicle, tripDetails, onContinue, availableVehicleCount = 2 }) => {
  const tripAmount = selectedVehicle?.price || 0;
  const extrasAmount = 0; // Will be added in extras step
  const totalAmount = calculateTotal(tripAmount, extrasAmount);

  // Generate WhatsApp URL with booking context
  const getWhatsAppUrl = () => {
    const phoneNumber = '905352385797';
    const message = encodeURIComponent(
      `Hi! I need help with my transfer booking. ` +
      `Route: ${tripDetails?.pickup || 'Airport'} -> ${tripDetails?.dropoff || 'Destination'} | ` +
      `Date: ${tripDetails?.date || 'Not set'} ${tripDetails?.time || ''} | ` +
      `Passengers: ${tripDetails?.passengers || 1} | Luggage: ${tripDetails?.luggage || 1}. ` +
      `I'm on the Vehicle step.`
    );
    return `https://wa.me/${phoneNumber}?text=${message}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="price-summary-card">
      {/* Orange top accent */}
      <div className="h-1 bg-[#FF8C00]" />

      <div className="p-6">
        <h3 className="font-bold text-black text-lg mb-4">Price Summary</h3>

        {/* Trust Bar - Above price */}
        <div className="mb-4">
          <TrustBar variant="compact" />
          {/* Extra micro-trust line */}
          <p className="text-center text-xs text-gray-500 mt-2">
            Instant confirmation • Licensed 3rd-party provider
          </p>
        </div>

        {/* Availability notice - softer styling */}
        {selectedVehicle && availableVehicleCount <= 3 && (
          <div className="flex items-center justify-center gap-1.5 mb-4 py-2 bg-amber-50 border border-amber-100 rounded-lg">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-700">
              {availableVehicleCount} vehicle{availableVehicleCount !== 1 ? 's' : ''} available for your selection
            </span>
          </div>
        )}

        {/* Price Breakdown - ALWAYS 2 decimals */}
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Trip</span>
            <span className="text-sm font-medium text-black">
              {selectedVehicle ? formatMoney(tripAmount) : '—'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Extras</span>
            <span className="text-sm font-medium text-gray-400">
              {formatMoney(extrasAmount)}
            </span>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-black">Total</span>
              {/* SAME exact price - NO rounding */}
              <span className="text-2xl font-bold text-black font-display">
                {selectedVehicle ? formatMoney(totalAmount) : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Selected Vehicle */}
        {selectedVehicle && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Selected Vehicle</p>
            <p className="text-sm font-semibold text-black">{selectedVehicle.name}</p>
          </div>
        )}

        {/* CTA Button - Enhanced for conversion */}
        <Button
          data-testid="continue-btn"
          className="w-full bg-orange-500 text-white hover:bg-orange-600 h-14 text-sm sm:text-base font-semibold transition-all duration-200 active:scale-[0.98] shadow-md hover:shadow-lg px-4"
          onClick={onContinue}
          disabled={!selectedVehicle}
        >
          <Check className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="truncate">Continue</span>
        </Button>

        {/* Microcopy - Payment not taken yet */}
        <p className="text-center text-xs text-gray-500 mt-2">
          You won't be charged yet
        </p>

        {/* Free Benefits Highlight */}
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg py-2">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-xs font-semibold">Free Cancellation (24h)</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg py-2">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            <span className="text-xs font-semibold">Free Meet & Greet Included</span>
          </div>
        </div>

        {/* WhatsApp Help Link - Subtle placement below CTA */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <a
            href={getWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-[#25D366] transition-colors"
            data-testid="whatsapp-help-link"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Need help? Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default PriceSummaryCard;
